import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { ApiMiddlewarePayload, ApiResponse } from '@ocean/api'
import { ToastUtils } from '@ocean/utils'
import { AppThunk } from 'store'
import { MiddlewareApiAction, createApiAction } from 'api'
import { LocalizationUtils } from 'utils'
import { userLoggedOut } from 'core/session/slices/session'
import {
  convertFromScormTimeToSeconds,
  getScorm2004Statuses,
} from 'scorm/helpers/scormRuntimeHelper'
import {
  ScormData,
  ScormDisplayData,
  WritableScormRuntimeData,
  CoreExit,
  SCORM12Status,
} from 'scorm/types'
import {
  getRapidElearningDetails,
  createRapidElearningResult,
  updateRapidElearningAttempt,
  startRapidElearningAttempt,
  closeRapidElearningResult,
} from 'modules/activityDetails/RapidElearningDetails/api/rapidElearningDetailsService'
import RapidElearningScormDataBuilder from 'modules/activityDetails/RapidElearningDetails/slices/helpers/rapidElearningScormDataBuilder'
import {
  ApiStartRapidElearningAttemptResponse,
  RapidElearningProgressUpdateDto,
  RapidElearningResultAttemptData,
} from 'modules/activityDetails/RapidElearningDetails/types'
import { RapidElearningDetailsState, ApiRapidElearningDetailsResponse } from './types'

const initialState: RapidElearningDetailsState = {
  rapidElearningDetailsData: {},
  runningChapterScromData: null,
  activeResultId: null,
  runningActivityId: null,
  runningAttemptId: null,
  commitsInProgress: 0,
  finishScheduled: false,
  scheduledFinishData: null,
  spinnerVisible: false,
  runChapterScheduled: false,
}

const rapidElearningDetailsSlice = createSlice({
  name: 'rapidElearningDetails',
  initialState,
  reducers: {
    rapidElearningDetailsLoaded(
      state,
      action: PayloadAction<ApiMiddlewarePayload<ApiRapidElearningDetailsResponse>>
    ) {
      state.rapidElearningDetailsData = action.payload
      state.activeResultId = action.payload.result?.rapidElearningDetails.result?.id || null
    },
    clearRapidElearningDetails(state) {
      state.rapidElearningDetailsData = initialState.rapidElearningDetailsData
    },
    setChapterToRunId(state, action) {
      state.runningActivityId = action.payload
    },
    resultCreated(state, action) {
      state.activeResultId = action.payload
    },
    scormDataCreated(state, action: PayloadAction<ScormData>) {
      state.runningChapterScromData = action.payload
    },
    closeChapter(state) {
      state.runningChapterScromData = null
      state.runningActivityId = null
      state.activeResultId = null
      state.commitsInProgress = 0
      state.finishScheduled = false
      state.scheduledFinishData = null
      state.runningAttemptId = null
    },
    increaseCommitsInProgress(state) {
      state.commitsInProgress += 1
    },
    decreaseCommitsInProgress(state) {
      if (state.commitsInProgress > 0) {
        state.commitsInProgress -= 1
      }
    },
    setFinishScheduled(state, action) {
      state.finishScheduled = action.payload
    },
    setScheduledFinishData(state, action) {
      state.scheduledFinishData = action.payload
    },
    showSpinner(state) {
      state.spinnerVisible = true
    },
    hideSpinner(state) {
      state.spinnerVisible = false
    },
    setRunningActivityAttemptId(state, action) {
      state.runningAttemptId = action.payload
    },
    noop() {},
  },
  extraReducers: builder => {
    builder.addCase(userLoggedOut, () => {
      return initialState
    })
  },
})

const {
  rapidElearningDetailsLoaded,
  clearRapidElearningDetails,
  setChapterToRunId,
  noop,
  resultCreated,
  closeChapter,
  increaseCommitsInProgress,
  scormDataCreated,
  setRunningActivityAttemptId,
  decreaseCommitsInProgress,
  setFinishScheduled,
  setScheduledFinishData,
  showSpinner,
  hideSpinner,
} = rapidElearningDetailsSlice.actions

export { clearRapidElearningDetails }
export default rapidElearningDetailsSlice.reducer

export const setRunningChapterId = setChapterToRunId

export const loadRapidElearningDetailsData = (rapidElearningId: Guid, nextAction?: any) => {
  return createApiAction({
    apiCalls: {
      rapidElearningDetails: () => getRapidElearningDetails(rapidElearningId),
    },
    payloadAction: rapidElearningDetailsLoaded,
    nextAction,
    getResultToKeep: state => state.rapidElearningDetails.rapidElearningDetailsData.result,
  })
}

export const createResult =
  (courseId: Guid, startChapter: boolean = false): AppThunk =>
  async dispatch => {
    dispatch(showSpinner())
    const response = await createRapidElearningResult(courseId)
    const { error, content }: ApiResponse<any> = response
    if (error || !content) {
      ToastUtils.error(LocalizationUtils.localizeError(error?.code))
      dispatch(hideSpinner())
      return
    }
    dispatch(resultCreated(content.resultId))
    dispatch(hideSpinner())
    if (startChapter) {
      dispatch(resetRapidElearningDetails(runChapterForNewResult))
    }
  }

const runChapterForNewResult =
  (response: ApiMiddlewarePayload<ApiRapidElearningDetailsResponse>): AppThunk =>
  async dispatch => {
    const { error, result } = response
    if (error || !result) {
      ToastUtils.error(LocalizationUtils.localizeError(error?.code))
      return
    }
    dispatch(rapidElearningDetailsLoaded(response))
    dispatch(runChapter())
  }

export const runChapter = (): AppThunk => async (dispatch, getState) => {
  const { runningActivityId, rapidElearningDetailsData } = getState().rapidElearningDetails
  if (!runningActivityId) return
  const runningChapter = rapidElearningDetailsData.result?.rapidElearningDetails.chapters.find(
    x => x.activityDefinitionId === runningActivityId
  )
  if (!runningChapter) return
  dispatch(startAttempt(runningChapter.activityResultId) as MiddlewareApiAction)
}

const startAttempt = (activityResultId: Guid) => {
  return createApiAction({
    apiCalls: {
      startRapidElearningAttempt: () => startRapidElearningAttempt(activityResultId),
    },
    payloadAction: showSpinner,
    nextAction: handleScormData,
  })
}

const handleScormData =
  (response: ApiMiddlewarePayload<ApiStartRapidElearningAttemptResponse>): AppThunk =>
  async (dispatch, getState) => {
    dispatch(hideSpinner())

    const { error, result } = response
    if (error || !result) {
      ToastUtils.error(LocalizationUtils.localizeError(error?.code))
      return
    }

    const rapidElearningDetailsState = getState().rapidElearningDetails
    const rapidElearningDetails =
      rapidElearningDetailsState.rapidElearningDetailsData.result?.rapidElearningDetails
    if (!rapidElearningDetails) {
      ToastUtils.error(LocalizationUtils.localizeError())
      return
    }

    const attemptData = result.startRapidElearningAttempt
    const runningChapterDefinition = rapidElearningDetails.chapters.find(
      x => x.activityDefinitionId === rapidElearningDetailsState.runningActivityId
    )
    if (!runningChapterDefinition) return
    const { moduleId, currentReleaseNumber } = rapidElearningDetails

    const elearningScormDataBuilder = new RapidElearningScormDataBuilder(
      runningChapterDefinition,
      attemptData,
      moduleId,
      currentReleaseNumber || 1
    )
    elearningScormDataBuilder.build()
    const scormData = rapidElearningDetails.scormData

    const scormRuntimeData = elearningScormDataBuilder.build()
    const { width, height, canBeResized, canBeMaximized }: ScormDisplayData = scormData

    const scormRunnerPayload = {
      scormRuntimeData,
      scormDisplayData: {
        height,
        width,
        isResponsive: true,
        canBeResized,
        canBeMaximized,
      },
    }
    dispatch(setRunningActivityAttemptId(attemptData.activityAttemptId))
    dispatch(scormDataCreated(scormRunnerPayload))
  }

export const commitChapterProgress =
  (data: WritableScormRuntimeData, finish?: boolean): AppThunk =>
  async (dispatch, getState) => {
    const { runningAttemptId, finishScheduled } = getState().rapidElearningDetails

    if (!runningAttemptId || (finishScheduled && !finish)) {
      return
    }

    const progressUpdateDto: RapidElearningProgressUpdateDto = {
      attemptData: translateScormValuesToUpdateAttemptDto(data, runningAttemptId),
      finishAttempt: !!finish,
    }

    const updateAction = createApiAction({
      apiCalls: {
        updateElearningProgress: () => updateRapidElearningAttempt(progressUpdateDto),
      },
      payloadAction: finish ? noop : increaseCommitsInProgress,
      nextAction: finish ? closeModule : checkScheduledFinish,
    })

    dispatch(updateAction)
  }

const translateScormValuesToUpdateAttemptDto = (
  scormData: WritableScormRuntimeData,
  activityAttemptId: Guid
): RapidElearningResultAttemptData => {
  const translatedStatuses = getScorm2004Statuses(scormData.LessonStatus as SCORM12Status)
  const score = parseInt(scormData.ScoreRaw)
  const activityAttempt: RapidElearningResultAttemptData = {
    activityAttemptId: activityAttemptId,
    completionStatus: translatedStatuses.completionStatus,
    successStatus: translatedStatuses.successStatus,
    coreExit: CoreExit[scormData.Exit],
    suspendData: scormData.SuspendData,
    lessonLocation: scormData.LessonLocation,
    score: isNaN(score) ? undefined : score,
    seconds: scormData.SessionTime ? convertFromScormTimeToSeconds(scormData.SessionTime) : 0,
  }
  return activityAttempt
}

export const checkScheduledFinish = (): AppThunk => async (dispatch, getState) => {
  dispatch(decreaseCommitsInProgress())
  const { commitsInProgress, finishScheduled, scheduledFinishData } =
    getState().rapidElearningDetails

  if (commitsInProgress === 0 && finishScheduled && scheduledFinishData) {
    dispatch(commitChapterProgress(scheduledFinishData, true))
  }
}

export const scheduleFinishChapter =
  (data: WritableScormRuntimeData): AppThunk =>
  async (dispatch, getState) => {
    const { commitsInProgress, finishScheduled } = getState().rapidElearningDetails
    if (finishScheduled) {
      // no other action can be triggered after finish has been scheduled
      return
    }

    dispatch(setFinishScheduled(true))
    if (commitsInProgress === 0) {
      dispatch(commitChapterProgress(data, true))
    } else {
      dispatch(setScheduledFinishData(data))
    }
  }

export const closeResult = (resultId: Guid) => {
  return createApiAction({
    apiCalls: {
      closeResult: () => closeRapidElearningResult(resultId),
    },
    payloadAction: showSpinner,
    nextAction: handleSubmitResult,
  })
}

const closeModule = (): AppThunk => async (dispatch, getState) => {
  const rapidElearningDetailsState = getState().rapidElearningDetails
  const rapidElearningDetailsData =
    rapidElearningDetailsState.rapidElearningDetailsData.result?.rapidElearningDetails
  const moduleId = rapidElearningDetailsData?.moduleId
  if (!moduleId) {
    return ToastUtils.error(LocalizationUtils.localizeError())
  }
  dispatch(closeChapter())
  dispatch(loadRapidElearningDetailsData(moduleId) as MiddlewareApiAction)
}

const handleSubmitResult =
  (response: ApiMiddlewarePayload): AppThunk =>
  async dispatch => {
    dispatch(hideSpinner())
    if (!response.error) dispatch(resetRapidElearningDetails())
  }

const resetRapidElearningDetails =
  (nextAction?: any): AppThunk =>
  async (dispatch, getState) => {
    dispatch(hideSpinner())
    const moduleId =
      getState().rapidElearningDetails.rapidElearningDetailsData.result?.rapidElearningDetails
        .moduleId
    if (!moduleId) return
    dispatch(loadRapidElearningDetailsData(moduleId, nextAction) as MiddlewareApiAction)
  }
